import React, { useState,useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
// Animated Login Page component
// - Tailwind CSS required
// - Framer Motion for subtle animations (already imported above)
// - Replace `mockLogin` with real API call when integrating

export default function Login({  
  accent = "#115e32",
  pageTitle = "Login | Cox's Bazar International University",
}) {
	useEffect(() => {
		document.title = pageTitle;
	}, [pageTitle]);
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [success, setSuccess] = useState(false);
	const navigate = useNavigate();
	const { login } = useAuth();

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError(null);
		setLoading(true);
		try {
			if (email.includes("@") && password.length >= 6) {	
			  const res = await login(email, password);
			  if (res.success) {
				setSuccess(true);
				// animate showing success then redirect
				setTimeout(() => {
				  // redirect to dashboard — replace path as needed
				   navigate("/dashboard");
				}, 900);
			  }else{
				  setError(res.message || "Login failed");
			  }
			}else{
				setError("Invalid Email or Password");
			}
		} catch (err) {
		  setError(err.message || "Login failed");
		  setSuccess(false);
		} finally {
		  setLoading(false);
		}
	};

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-sky-50 to-white p-6">
      {/* Decorative SVG blobs and subtle background */}
      <svg
        className="pointer-events-none absolute left-0 top-0 w-full h-full opacity-20"
        viewBox="0 0 1440 600"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        <defs>
          <linearGradient id="g1" x1="0" x2="1">
            <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#7c3aed" stopOpacity="0.25" />
          </linearGradient>
        </defs>
        <path fill="url(#g1)" d="M0,160 C220,260 360,40 720,120 C1080,200 1240,340 1440,300 L1440,600 L0,600 Z" />
        <circle cx="1100" cy="80" r="60" fill="#7c3aed" opacity="0.07">
          <animate attributeName="r" values="55;65;55" dur="6s" repeatCount="indefinite" />
        </circle>
      </svg>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 w-full max-w-md bg-white/80 backdrop-blur-sm border border-slate-200 rounded-2xl shadow-2xl px-8 py-10"
      >
        {/* Header with small svg logo */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-violet-600 flex items-center justify-center shadow-md">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M3 12h18" stroke="white" strokeWidth="1.6" strokeLinecap="round" />
              <path d="M3 6h18" stroke="white" strokeWidth="1.6" strokeLinecap="round" opacity="0.6" />
            </svg>
          </div>
          <div>
            <h1 className="text-lg font-semibold text-slate-800">Welcome back</h1>
            <p className="text-sm text-slate-500">Login to your account to continue</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full rounded-lg border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-sky-300 shadow-sm"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="w-full rounded-lg border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-sky-300 shadow-sm"
              placeholder="Enter password"
            />
          </div>

          <div className="flex items-center justify-between text-sm text-slate-600">
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" className="rounded-sm" /> <span>Remember me</span>
            </label>
            <a href="/forget-password" className="hover:underline">
              Forgot?
            </a>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex justify-center items-center gap-2 rounded-lg px-4 py-3 bg-gradient-to-r from-sky-500 to-violet-600 text-white font-medium shadow-md hover:scale-[1.01] transform transition"
            >
              {loading ? (
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="white" strokeWidth="4" fill="none"></circle>
                  <path className="opacity-75" fill="white" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                </svg>
              ) : null}
              <span>{loading ? "Signing in..." : "Sign in"}</span>
            </button>
          </div>
        </form>

        {/* Inline notifications */}
        <div className="mt-4">
          {error ? (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-md bg-red-50 border border-red-100 text-red-700 px-4 py-3 text-sm"
            >
              {error}
            </motion.div>
          ) : null}

          {success ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.25 }}
              className="rounded-md bg-emerald-50 border border-emerald-100 text-emerald-700 px-4 py-3 text-sm mt-2 flex items-center justify-between"
            >
              <div>Login successful — redirecting to dashboard...</div>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M20 6L9 17l-5-5" stroke="#065f46" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </motion.div>
          ) : null}
        </div>

        {/* Small footer */}
        <div className="mt-6 text-center text-xs text-slate-400">By signing in you agree to the Terms of Service</div>
      </motion.div>

      {/* Floating animated shapes — positioned outside of form container for depth */}
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: [0.95, 1.02, 0.98], opacity: 0.9 }}
        transition={{ duration: 6, repeat: Infinity, repeatType: "mirror" }}
        className="pointer-events-none absolute right-6 bottom-12 w-52 h-52 rounded-3xl bg-gradient-to-br from-sky-400 to-violet-500 blur-3xl opacity-30"
      />

      <style>
        {`
          /* Extra small animation for inputs on focus */
          input:focus {
            outline: none;
            box-shadow: 0 8px 24px rgba(14, 165, 233, 0.06);
          }

          /* Make form slightly raised on hover */
          .form-hover:hover {
            transform: translateY(-2px);
          }
        `}
      </style>
    </div>
  );
}
